import React from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  PersonPin as HotSeatIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as TimeIcon,
  QuestionAnswer as QAIcon,
  EmojiEvents as TrophyIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

const BeerillionerHistoryTab = (props) => {
  const {
    loadHistory,
    historyLoading,
    historySessions,
    expandedSession,
    loadSessionDetail,
    detailLoading,
    sessionDetail,
  } = props;

  return (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}>
              <Typography
                variant="h6"
                sx={{ color: "#fff" }}>
                Game History
              </Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadHistory}
                sx={{ color: "#ffd700", borderColor: "#ffd700" }}>
                Refresh
              </Button>
            </Box>
            {historyLoading ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <LinearProgress
                  sx={{
                    bgcolor: "#333",
                    "& .MuiLinearProgress-bar": { bgcolor: "#ffd700" },
                  }}
                />
              </Box>
            ) : historySessions.length === 0 ? (
              <Paper sx={{ p: 4, bgcolor: "#1a1a1a", textAlign: "center" }}>
                <Typography sx={{ color: "#666" }}>
                  No completed game sessions yet.
                </Typography>
              </Paper>
            ) : (
              (() => {
                const grouped = {};
                historySessions.forEach((s) => {
                  const d = s.created_at
                    ? new Date(
                        s.created_at.replace(" ", "T") +
                          (s.created_at.includes("T") ? "" : "+05:30"),
                      )
                    : new Date();
                  const key = d.toLocaleDateString("en-IN", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  });
                  if (!grouped[key]) grouped[key] = [];
                  grouped[key].push({ ...s, _date: d });
                });
                return Object.entries(grouped).map(([date, sessions]) => (
                  <Box
                    key={date}
                    sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: "#ffd700",
                        fontWeight: 700,
                        mb: 1,
                        borderBottom: "1px solid #333",
                        pb: 0.5,
                      }}>
                      {date}
                    </Typography>
                    {sessions.map((s) => {
                      const isExpanded = expandedSession === s.id;
                      const startTime = s._date.toLocaleTimeString("en-IN", {
                        hour: "2-digit",
                        minute: "2-digit",
                      });
                      const endTime = s.ended_at
                        ? new Date(s.ended_at).toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—";
                      return (
                        <Paper
                          key={s.id}
                          sx={{
                            mb: 1,
                            bgcolor: "#1a1a1a",
                            border: isExpanded
                              ? "1px solid #ffd700"
                              : "1px solid #333",
                            borderRadius: 1,
                            overflow: "hidden",
                          }}>
                          <Box
                            onClick={() => loadSessionDetail(s.id)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              p: 2,
                              cursor: "pointer",
                              "&:hover": { bgcolor: "#222" },
                            }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flex: 1,
                              }}>
                              <TimeIcon sx={{ color: "#888", fontSize: 18 }} />
                              <Typography
                                variant="body2"
                                sx={{ color: "#aaa" }}>
                                {startTime} — {endTime}
                              </Typography>
                              <Chip
                                label={s.game_name || "Beerillioner"}
                                size="small"
                                sx={{
                                  bgcolor: "#333",
                                  color: "#ffd700",
                                  fontWeight: 600,
                                }}
                              />
                              <Chip
                                label={`#${s.id}`}
                                size="small"
                                sx={{ bgcolor: "#222", color: "#888" }}
                              />
                              <Chip
                                label={
                                  s.session_mode === "random"
                                    ? "Random"
                                    : "Pre-select"
                                }
                                size="small"
                                sx={{
                                  bgcolor:
                                    s.session_mode === "random"
                                      ? "rgba(230,81,0,0.2)"
                                      : "rgba(255,215,0,0.1)",
                                  color:
                                    s.session_mode === "random"
                                      ? "#e65100"
                                      : "#ffd700",
                                }}
                              />
                            </Box>
                            {s.hot_seat_display_name && (
                              <Chip
                                icon={<HotSeatIcon />}
                                label={s.hot_seat_display_name}
                                size="small"
                                sx={{ bgcolor: "#b8860b", color: "#fff" }}
                              />
                            )}
                            <Chip
                              label={`Level ${s.current_level || 0}`}
                              size="small"
                              sx={{ bgcolor: "#4a6741", color: "#fff" }}
                            />
                            {s.walk_away ? (
                              <Chip
                                label="Walked Away"
                                size="small"
                                sx={{
                                  bgcolor: "rgba(255,152,0,0.2)",
                                  color: "#ff9800",
                                }}
                              />
                            ) : null}
                            {isExpanded ? (
                              <ExpandLessIcon sx={{ color: "#ffd700" }} />
                            ) : (
                              <ExpandMoreIcon sx={{ color: "#888" }} />
                            )}
                          </Box>

                          {isExpanded && (
                            <Box sx={{ borderTop: "1px solid #333", p: 2 }}>
                              {detailLoading ? (
                                <Box sx={{ textAlign: "center", py: 3 }}>
                                  <LinearProgress
                                    sx={{
                                      bgcolor: "#333",
                                      "& .MuiLinearProgress-bar": {
                                        bgcolor: "#ffd700",
                                      },
                                    }}
                                  />
                                </Box>
                              ) : sessionDetail ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 3,
                                  }}>
                                  {/* FFF Rounds */}
                                  {sessionDetail.fffRounds &&
                                    sessionDetail.fffRounds.length > 0 && (
                                      <Box>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            color: "#1565c0",
                                            fontWeight: 700,
                                            mb: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}>
                                          <QAIcon fontSize="small" />
                                          Fastest Finger First
                                        </Typography>
                                        {sessionDetail.fffRounds.map(
                                          (round, ri) => (
                                            <Paper
                                              key={round.id || ri}
                                              sx={{
                                                p: 2,
                                                bgcolor: "#0d1b2a",
                                                mb: 1,
                                                borderRadius: 1,
                                                border: "1px solid #1565c0",
                                              }}>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  color: "#90caf9",
                                                  fontWeight: 600,
                                                  mb: 1,
                                                }}>
                                                Round {round.round_number}:{" "}
                                                {round.question_text}
                                              </Typography>
                                              <Grid
                                                container
                                                spacing={1}
                                                sx={{ mb: 1 }}>
                                                {["A", "B", "C", "D"].map(
                                                  (o) => (
                                                    <Grid
                                                      item
                                                      xs={6}
                                                      key={o}>
                                                      <Typography
                                                        variant="caption"
                                                        sx={{
                                                          color:
                                                            round.correct_answer ===
                                                            o
                                                              ? "#4caf50"
                                                              : "#aaa",
                                                        }}>
                                                        {o}:{" "}
                                                        {
                                                          round[
                                                            `option_${o.toLowerCase()}`
                                                          ]
                                                        }
                                                        {round.correct_answer ===
                                                        o
                                                          ? " ✓"
                                                          : ""}
                                                      </Typography>
                                                    </Grid>
                                                  ),
                                                )}
                                              </Grid>
                                              {round.timer_seconds && (
                                                <Typography
                                                  variant="caption"
                                                  sx={{ color: "#666" }}>
                                                  Timer: {round.timer_seconds}s
                                                </Typography>
                                              )}
                                              {round.answers &&
                                                round.answers.length > 0 && (
                                                  <Box
                                                    sx={{
                                                      mt: 1,
                                                      borderTop:
                                                        "1px solid #1a2a3a",
                                                      pt: 1,
                                                    }}>
                                                    <Typography
                                                      variant="caption"
                                                      sx={{
                                                        color: "#888",
                                                        fontWeight: 600,
                                                      }}>
                                                      Answers (
                                                      {round.answers.length})
                                                    </Typography>
                                                    {round.answers.map(
                                                      (a, ai) => (
                                                        <Box
                                                          key={a.id || ai}
                                                          sx={{
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                            gap: 1,
                                                            mt: 0.5,
                                                            pl: 1,
                                                          }}>
                                                          <Typography
                                                            variant="caption"
                                                            sx={{
                                                              color:
                                                                a.is_correct
                                                                  ? "#4caf50"
                                                                  : "#ef5350",
                                                              minWidth: 16,
                                                            }}>
                                                            {a.is_correct
                                                              ? "✓"
                                                              : "✗"}
                                                          </Typography>
                                                          <Typography
                                                            variant="caption"
                                                            sx={{
                                                              color: "#fff",
                                                              minWidth: 100,
                                                            }}>
                                                            {a.display_name}
                                                          </Typography>
                                                          <Chip
                                                            label={a.answer}
                                                            size="small"
                                                            sx={{
                                                              bgcolor:
                                                                a.is_correct
                                                                  ? "rgba(76,175,80,0.2)"
                                                                  : "rgba(239,83,80,0.2)",
                                                              color:
                                                                a.is_correct
                                                                  ? "#4caf50"
                                                                  : "#ef5350",
                                                              height: 20,
                                                              fontSize:
                                                                "0.7rem",
                                                            }}
                                                          />
                                                          <Typography
                                                            variant="caption"
                                                            sx={{
                                                              color: "#666",
                                                            }}>
                                                            {a.answered_at
                                                              ? new Date(
                                                                  a.answered_at.replace(
                                                                    " ",
                                                                    "T",
                                                                  ) +
                                                                    (a.answered_at.includes(
                                                                      "T",
                                                                    )
                                                                      ? ""
                                                                      : "+05:30"),
                                                                ).toLocaleTimeString(
                                                                  "en-IN",
                                                                  {
                                                                    hour: "2-digit",
                                                                    minute:
                                                                      "2-digit",
                                                                    second:
                                                                      "2-digit",
                                                                  },
                                                                )
                                                              : ""}
                                                          </Typography>
                                                        </Box>
                                                      ),
                                                    )}
                                                  </Box>
                                                )}
                                            </Paper>
                                          ),
                                        )}
                                      </Box>
                                    )}

                                  {/* Main Game */}
                                  {sessionDetail.mainGameAnswers &&
                                    sessionDetail.mainGameAnswers.length >
                                      0 && (
                                      <Box>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            color: "#ffd700",
                                            fontWeight: 700,
                                            mb: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}>
                                          <TrophyIcon fontSize="small" />
                                          Main Game —{" "}
                                          {sessionDetail.session
                                            ?.hot_seat_display_name ||
                                            "Unknown Player"}
                                        </Typography>
                                        {sessionDetail.mainGameAnswers.map(
                                          (q, qi) => (
                                            <Paper
                                              key={q.id || qi}
                                              sx={{
                                                p: 2,
                                                bgcolor: q.is_correct
                                                  ? "rgba(46,125,50,0.08)"
                                                  : "rgba(198,40,40,0.08)",
                                                mb: 1,
                                                borderRadius: 1,
                                                border: `1px solid ${q.is_correct ? "#2e7d32" : "#c62828"}`,
                                              }}>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  alignItems: "center",
                                                  mb: 1,
                                                }}>
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                  }}>
                                                  <Chip
                                                    label={`Level ${q.level}`}
                                                    size="small"
                                                    sx={{
                                                      bgcolor: "#4a6741",
                                                      color: "#fff",
                                                      fontWeight: 700,
                                                    }}
                                                  />
                                                  {q.question_source ===
                                                    "quiz" && (
                                                    <Chip
                                                      label="Quiz"
                                                      size="small"
                                                      sx={{
                                                        bgcolor: "#1565c0",
                                                        color: "#fff",
                                                      }}
                                                    />
                                                  )}
                                                </Box>
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                  }}>
                                                  {q.time_taken_seconds !=
                                                    null && (
                                                    <Typography
                                                      variant="caption"
                                                      sx={{ color: "#888" }}>
                                                      {q.time_taken_seconds}s
                                                    </Typography>
                                                  )}
                                                  {q.is_correct ? (
                                                    <CheckIcon
                                                      sx={{
                                                        color: "#4caf50",
                                                        fontSize: 20,
                                                      }}
                                                    />
                                                  ) : (
                                                    <CancelIcon
                                                      sx={{
                                                        color: "#ef5350",
                                                        fontSize: 20,
                                                      }}
                                                    />
                                                  )}
                                                </Box>
                                              </Box>
                                              <Typography
                                                variant="body2"
                                                sx={{ color: "#fff", mb: 1 }}>
                                                {q.question_text}
                                              </Typography>
                                              <Grid
                                                container
                                                spacing={1}>
                                                {["A", "B", "C", "D"].map(
                                                  (o) => {
                                                    const isCorrect =
                                                      q.correct_answer === o;
                                                    const isPlayer =
                                                      q.player_answer === o;
                                                    let bg = "#1a1a1a";
                                                    if (isCorrect)
                                                      bg =
                                                        "rgba(76,175,80,0.15)";
                                                    if (isPlayer && !isCorrect)
                                                      bg =
                                                        "rgba(239,83,80,0.15)";
                                                    return (
                                                      <Grid
                                                        item
                                                        xs={6}
                                                        key={o}>
                                                        <Box
                                                          sx={{
                                                            p: 0.5,
                                                            px: 1,
                                                            borderRadius: 0.5,
                                                            bgcolor: bg,
                                                            border: isPlayer
                                                              ? "1px solid " +
                                                                (isCorrect
                                                                  ? "#4caf50"
                                                                  : "#ef5350")
                                                              : "1px solid transparent",
                                                          }}>
                                                          <Typography
                                                            variant="caption"
                                                            sx={{
                                                              color: isCorrect
                                                                ? "#4caf50"
                                                                : isPlayer
                                                                  ? "#ef5350"
                                                                  : "#aaa",
                                                            }}>
                                                            <strong>
                                                              {o}:
                                                            </strong>{" "}
                                                            {
                                                              q[
                                                                `option_${o.toLowerCase()}`
                                                              ]
                                                            }
                                                            {isCorrect
                                                              ? " ✓"
                                                              : ""}
                                                            {isPlayer &&
                                                            !isCorrect
                                                              ? " ✗ (selected)"
                                                              : isPlayer &&
                                                                  isCorrect
                                                                ? " (selected)"
                                                                : ""}
                                                          </Typography>
                                                        </Box>
                                                      </Grid>
                                                    );
                                                  },
                                                )}
                                              </Grid>
                                              {(() => {
                                                let used = [];
                                                try {
                                                  used = JSON.parse(
                                                    q.lifelines_used || "[]",
                                                  );
                                                } catch {
                                                  used = [];
                                                }
                                                if (used.length === 0)
                                                  return null;
                                                return (
                                                  <Box
                                                    sx={{
                                                      mt: 1,
                                                      display: "flex",
                                                      gap: 0.5,
                                                      alignItems: "center",
                                                    }}>
                                                    <Typography
                                                      variant="caption"
                                                      sx={{ color: "#888" }}>
                                                      Lifelines:
                                                    </Typography>
                                                    {used.map((l) => (
                                                      <Chip
                                                        key={l}
                                                        label={l.replace(
                                                          "_",
                                                          " ",
                                                        )}
                                                        size="small"
                                                        sx={{
                                                          bgcolor: "#333",
                                                          color: "#ffd700",
                                                          fontSize: "0.65rem",
                                                          height: 18,
                                                        }}
                                                      />
                                                    ))}
                                                  </Box>
                                                );
                                              })()}
                                            </Paper>
                                          ),
                                        )}
                                      </Box>
                                    )}

                                  {(!sessionDetail.fffRounds ||
                                    sessionDetail.fffRounds.length === 0) &&
                                    (!sessionDetail.mainGameAnswers ||
                                      sessionDetail.mainGameAnswers.length ===
                                        0) && (
                                      <Typography
                                        sx={{
                                          color: "#666",
                                          textAlign: "center",
                                          py: 2,
                                        }}>
                                        No detailed history recorded for this
                                        session.
                                      </Typography>
                                    )}
                                </Box>
                              ) : (
                                <Typography
                                  sx={{
                                    color: "#666",
                                    textAlign: "center",
                                    py: 2,
                                  }}>
                                  Failed to load session details.
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                ));
              })()
            )}
          </Box>
  );
};

export default BeerillionerHistoryTab;
